import {
    CampaignManagerInfluencer,
    getAllInfluencerCosts,
    getCampaignManagerInfluencers,
    InfluencerCost,
    InstagramUserImage,
    TiktokUserImage,
} from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { fetchUserImages } from '../../influencer/roster/RosterList/helpers';

type CampaignManagersDataResult = {
    influencers: CampaignManagerInfluencer[];
    influencerCosts: InfluencerCost[];
    tiktokUserImages: TiktokUserImage[];
    instagramUserImages: InstagramUserImage[];
    influencerCount: number | null;
    hasNextInfluencersPage: boolean;
    isLoading: boolean;
    hasErrorLoadingData: boolean;
    isInitialized: boolean;
    init: (requestInit?: RequestInit) => Promise<void>;
    reset: () => void;
    loadNextPage: (requestInit?: RequestInit) => Promise<void>;
    updateInfluencers: (influencer: CampaignManagerInfluencer) => void;
    refreshInfluencerCosts: (influencerId: number) => Promise<void>;
    addInfluencer: (influencer: CampaignManagerInfluencer) => void;
    addCosts: (costs: InfluencerCost[]) => void;
};

export function useCampaignManagerInfluencersData({
    page_size,
    search,
}: Parameters<typeof getCampaignManagerInfluencers>[0]): CampaignManagersDataResult {
    const [influencers, setInfluencers] = useState<CampaignManagerInfluencer[]>([]);
    const [influencerCount, setInfluencerCount] = useState<number | null>(null);
    const [hasNextInfluencersPage, setHasNextInfluencersPage] = useState(false);
    const [page, setPage] = useState(1);

    const [influencerCosts, setInfluencerCosts] = useState<InfluencerCost[]>([]);

    const [tiktokUserImages, setTiktokUserImages] = useState<TiktokUserImage[]>([]);
    const [instagramUserImages, setInstagramUserImages] = useState<InstagramUserImage[]>([]);

    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoadingData, setHasErrorLoadingData] = useState(false);

    const fetchInfluencerData = useCallback(async (...params: Parameters<typeof getCampaignManagerInfluencers>) => {
        try {
            setIsLoading(true);
            setHasErrorLoadingData(false);
            const response = await getCampaignManagerInfluencers(...params);

            if (response.status === 404) {
                setHasErrorLoadingData(true);
                return;
            }

            const {
                data: { next, results: influencers, count },
            } = response;

            setInfluencers((prev) => prev.concat(influencers));
            setInfluencerCount(count);
            setHasNextInfluencersPage(Boolean(next));

            if (!influencers.length) {
                return;
            }

            const influencerIds = influencers.map((inf) => inf.id);
            const costData = await getAllInfluencerCosts({ id: influencerIds.join() });

            setInfluencerCosts((prev) => prev.concat(costData));

            const tiktokUserIds = influencers.map((inf) => inf.tiktok_user_id).filter((id): id is number => !!id);

            const instagramUserIds = influencers.map((inf) => inf.instagram_user_id).filter((id): id is number => !!id);

            const [tiktokUserImages, instagramUserImages] = await fetchUserImages({
                tiktokUserIds,
                instagramUserIds,
            });

            setTiktokUserImages((prev) => prev.concat(tiktokUserImages));
            setInstagramUserImages((prev) => prev.concat(instagramUserImages));
        } catch (e) {
            setHasErrorLoadingData(true);
            throw e;
        } finally {
            setIsLoading(false);
            setIsInitialized(true);
        }
    }, []);

    const loadNextPage = useCallback(
        async (requestInit?: RequestInit) => {
            await fetchInfluencerData({ page: page + 1, page_size, search }, requestInit);
            setPage((prev) => prev + 1);
        },
        [fetchInfluencerData, page, page_size, search]
    );

    const debouncedLoadNextPage = useMemo(
        () => debounce(loadNextPage, 500, { leading: true, trailing: false }),
        [loadNextPage]
    );

    const init = useCallback(
        async (requestInit?: RequestInit) => {
            await fetchInfluencerData({ page, page_size, search }, requestInit);
        },
        [fetchInfluencerData, page, page_size, search]
    );

    const reset = useCallback(() => {
        setInfluencers([]);
        setInfluencerCosts([]);
        setInstagramUserImages([]);
        setTiktokUserImages([]);
        setInfluencerCount(null);
        setHasNextInfluencersPage(false);
        setPage(1);
        setIsInitialized(false);
        setIsLoading(false);
        setHasErrorLoadingData(false);
    }, []);

    return {
        influencers,
        influencerCount,
        influencerCosts,
        hasNextInfluencersPage,
        isInitialized,
        isLoading,
        hasErrorLoadingData,
        instagramUserImages,
        tiktokUserImages,
        init,
        reset,
        loadNextPage: debouncedLoadNextPage,
        updateInfluencers: (influencer) => {
            setInfluencers((prev) => prev.map((inf) => (inf.id === influencer.id ? influencer : inf)));
        },
        refreshInfluencerCosts: async (influencerId) => {
            const influencer = influencers.find((inf) => inf.id === influencerId);
            if (!influencer) {
                return;
            }

            try {
                const costs = await getAllInfluencerCosts({ id: influencerId.toString() });
                setInfluencerCosts((prev) => prev.filter((cost) => cost.influencer !== influencerId).concat(costs));
            } catch {
                throw new Error('Could not refresh influencer costs');
            }
        },
        addInfluencer: (influencer) => {
            setInfluencers((prev) => [influencer, ...prev]);
        },
        addCosts: (costs) => {
            setInfluencerCosts((prev) => prev.concat(costs));
        },
    };
}
